/**
 * The account menu's entries — ONE list, read by today's dropdown (src/components/ProfileImage.tsx) and the new
 * design's header (its avatar menu and the phone's sheet), so the two can never offer different places or disagree
 * about who may see a billing page.
 *
 * The rules, each one the menu has always had:
 * - the page you are on is not offered;
 * - wallet and subscription follow the billing.view gate (`canSeeWallet`, fail-open until the permission resolves),
 *   activity the activity.view gate (`canSeeActivity`) — a member an admin restricted gets no path to the access wall
 *   (ORG-USAGE-2026, task #77); a direct address still lands on the honest wall;
 * - activity sits between wallet and profile; team & roles matches its sub-pages, integrations its sub-pages;
 * - skills (the procedures Prest0n follows here, skills PR J) sits after team & roles: every member may open it,
 *   and the page itself shows each person only the sections their role allows;
 * - the insights studio (insights studio S2, 2026-10-16) sits after skills, offered only in Prest0 Inc.'s workspace to
 *   someone holding insights.posts.write (`canSeeInsightsStudio`, src/hooks/useInsightsStudioAccess.ts, from the same
 *   useUsageVisibility answer); absent means not offered. It hides chrome only: the route's DeveloperRoute and every
 *   callable decide;
 * - no entry changes the light: the account menu carries no theme control (D-66,
 *   tests/landing/accountMenuHasNoThemeRow.test.tsx).
 *
 * Logout is not an entry: it is an action, and each surface draws it last, apart from the places.
 */
import { useLocation } from 'react-router-dom';
import { useUsageVisibility } from './useUsageVisibility';
import { canSeeInsightsStudio, useActiveWorkspaceAccountId } from './useInsightsStudioAccess';

export type AccountMenuId =
  | 'desktop'
  | 'wallet'
  | 'activity'
  | 'profile'
  | 'settings'
  | 'team'
  | 'skills'
  | 'insights-studio'
  | 'organization'
  | 'integrations'
  | 'subscription';

export interface AccountMenuEntry {
  id: AccountMenuId;
  label: string;
  to: string;
}

export interface AccountMenuFacts {
  pathname: string;
  canSeeWallet: boolean;
  canSeeActivity: boolean;
  /** Prest0 Inc.'s workspace and insights.posts.write; absent means the studio is not offered. */
  canSeeInsightsStudio?: boolean;
}

/** The entries for these facts, in the menu's order. Pure. */
export function accountMenuEntries({ pathname, canSeeWallet, canSeeActivity, canSeeInsightsStudio = false }: AccountMenuFacts): AccountMenuEntry[] {
  const here = (to: string) => pathname === to;
  const under = (to: string) => pathname.startsWith(to);
  const out: AccountMenuEntry[] = [];
  if (!here('/desktop')) out.push({ id: 'desktop', label: 'desktop', to: '/desktop' });
  if (canSeeWallet && !here('/wallet')) out.push({ id: 'wallet', label: 'wallet', to: '/wallet' });
  if (canSeeActivity && !here('/activity')) out.push({ id: 'activity', label: 'activity', to: '/activity' });
  if (!here('/profile')) out.push({ id: 'profile', label: 'profile', to: '/profile' });
  if (!here('/settings')) out.push({ id: 'settings', label: 'settings', to: '/settings' });
  if (!under('/settings/team')) out.push({ id: 'team', label: 'team & roles', to: '/settings/team' });
  if (!here('/settings/skills')) out.push({ id: 'skills', label: 'skills', to: '/settings/skills' });
  if (canSeeInsightsStudio && !under('/admin/insights')) out.push({ id: 'insights-studio', label: 'insights studio', to: '/admin/insights' });
  if (!here('/organization')) out.push({ id: 'organization', label: 'organization', to: '/organization' });
  if (!under('/integrations')) out.push({ id: 'integrations', label: 'integrations', to: '/integrations' });
  if (canSeeWallet && !here('/subscription')) out.push({ id: 'subscription', label: 'subscription', to: '/subscription' });
  return out;
}

/** The entries for the signed-in person on the page they are on. */
export function useAccountMenuItems(): AccountMenuEntry[] {
  const { pathname } = useLocation();
  const visibility = useUsageVisibility();
  const accountId = useActiveWorkspaceAccountId();
  return accountMenuEntries({
    pathname,
    canSeeWallet: visibility.canSeeWallet,
    canSeeActivity: visibility.canSeeActivity,
    canSeeInsightsStudio: canSeeInsightsStudio(accountId, visibility),
  });
}
